import { useQueryClient } from "@tanstack/react-query";
import * as DateFns from "date-fns";
import _ from "lodash";
import React, { useMemo } from "react";
import { TagsPopoverProps } from "src/components/Tags";
import { Mode } from "src/components/Tags/types";
import * as FSQ from "src/generated/hooks/form-service/queries";
import * as FSR from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import useRequiredHasuraRoles from "src/hooks/useRequiredHasuraRoles";
import * as List from "src/services/list";
import { isNotNull } from "src/services/predicates";
import { HasuraRole } from "src/types/hasuraRole";
import { WithId } from "src/types/id";
import * as RemoteData from "src/types/remoteData";
import { Selected, Tag } from "src/types/tags";
import { FormId } from "../forms/types";
import { useOptimisticUpdate } from "./useOptimisticUpdate";

import { useDeleteTag, useEditTag, useNewTag } from "./useTagsFS";

const BATCH_SIZE = 1000;

type Props = {
  enrollmentPeriodId: uuid;
};

type ReturnType = {
  tagsProps: TagsPopoverProps;
  setSelectedRows: (
    selectedRows: RemoteData.RemoteData<unknown, FormId[]>
  ) => void;
  isDirty: boolean;
  resetFormTags: () => void;
};

export function useFormTags(
  { enrollmentPeriodId }: Props,
  skip: boolean = false
): ReturnType {
  const queryClient = useQueryClient();
  const [isDirty, setIsDirty] = React.useState(false);
  const [selectedRows, setSelectedRows] = React.useState<
    RemoteData.RemoteData<unknown, FormId[]>
  >(RemoteData.loading());

  const invalidateQueries = React.useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [FSQ.useFetchFormTagsKey],
    });
    await queryClient.invalidateQueries({
      queryKey: [FSQ.useFetchTagsKey],
    });
    await queryClient.invalidateQueries({
      queryKey: [FSQ.useListFormsKey],
    });
  }, [queryClient]);

  const getTagGroupsResponse = FSQ.useFetchTagGroups(
    {
      query: {
        enrollment_period_id: enrollmentPeriodId,
        is_default: true,
      },
    },
    [enrollmentPeriodId],
    {
      enabled: isNotNull(enrollmentPeriodId) && !skip,
    }
  );

  const internalTagGroupId = useMemo(() => {
    if (getTagGroupsResponse.isPending) return null;
    return getTagGroupsResponse.data?.data.tag_groups[0]?.id ?? null;
  }, [getTagGroupsResponse]);

  const enrollmentPeriodTagsResponse = FSQ.useFetchTags(
    {
      query: {
        enrollment_period_id: enrollmentPeriodId,
        tag_group_id: internalTagGroupId ?? undefined,
      },
    },
    [enrollmentPeriodId, internalTagGroupId],
    {
      enabled:
        isNotNull(enrollmentPeriodId) && isNotNull(internalTagGroupId) && !skip,
    }
  );
  const enrollmentPeriodTags = RemoteData.fromRestResult(
    enrollmentPeriodTagsResponse
  ).map(({ data }) => data);

  const [selectedFormIds, setSelectedFormIds] = React.useState<string[]>([]);
  const getFormTagsResponse = FSQ.useFetchFormTags(
    {
      query: {
        form_ids: selectedFormIds,
      },
    },
    [selectedFormIds],
    {
      enabled: selectedFormIds && selectedFormIds.length > 0 && !skip,
    }
  );
  const formTags = RemoteData.fromRestResult(getFormTagsResponse).map(
    ({ data }) => data
  );

  const { mutateAsync: addFormTag } = FSQ.useAddFormTag(undefined, {
    onSuccess: (data) => {
      if ("error" in data) throw new Error(data.error?.error.message);
    },
  });

  const { mutateAsync: removeFormTag } = FSQ.useRemoveFormTag(undefined, {
    onSuccess: (data) => {
      if ("error" in data) throw new Error(data.error?.error.message);
    },
  });

  const [searchKeyword, setSearchKeyword] = React.useState("");
  const onSearch = async (keyword: string) => {
    setSearchKeyword(keyword);
  };
  const filterByKeyword = React.useCallback(
    (tagName: string): boolean => {
      if (searchKeyword.trim() === "") return true;
      return tagName.toLowerCase().includes(searchKeyword.toLowerCase());
    },
    [searchKeyword]
  );

  const {
    optimisticUpdateMap,
    applyTagOptimistically,
    removeTagOptimistically,
    resetOptimisticUpdate,
  } = useOptimisticUpdate();

  const tags: RemoteData.RemoteData<unknown, Tag<WithId>[]> =
    React.useMemo(() => {
      return RemoteData.toTuple3(
        enrollmentPeriodTags,
        formTags,
        selectedRows
      ).map(([tags, allTags, rows]) => {
        const tagsCounter = countTags(tags, allTags);
        return List.filterMap(tags, (tag) => {
          if (!filterByKeyword(tag.name)) {
            return null;
          }

          let selected: Selected;
          let isUpdating = false;
          const optimisticSelected = optimisticUpdateMap.get(tag.id);
          if (optimisticSelected) {
            isUpdating = true;
            selected = optimisticSelected;
          } else {
            const totalFormSchool = rows.length;
            const selectedCount = tagsCounter.get(tag.id);
            selected =
              selectedCount === 0
                ? "None"
                : selectedCount === totalFormSchool
                ? "All"
                : "Partial";
          }

          return {
            id: tag.id,
            name: tag.name,
            description: tag.description ?? null,
            lastUsedAt: tag.last_used_at
              ? DateFns.parseISO(tag.last_used_at)
              : null,
            selected,
            isUpdating,
          };
        });
      });
    }, [
      enrollmentPeriodTags,
      formTags,
      selectedRows,
      filterByKeyword,
      optimisticUpdateMap,
    ]);

  const toast = useAvelaToast();

  const applyTag = React.useCallback(
    async (tagId: string) => {
      try {
        if (!selectedRows.hasData())
          throw new Error("Unable to apply tags since no rows are selected");
        // do this in batch for better performance
        // benchmark data:
        // without batch: 5000 rows => 17.36s
        // batch (1000): 5000 rows => 3.78s
        const chunk = _.chunk(selectedRows.data, BATCH_SIZE);
        await Promise.all(
          chunk.map(async (rows) => {
            await addFormTag({
              body: {
                data: {
                  form_ids: rows.map((row) => row.formId),
                  tag_ids: [tagId],
                },
              },
            });
          })
        );
        await invalidateQueries();
        setIsDirty(true);
      } catch (error) {
        console.error(error);
        toast.error({ title: "Unable to apply tags" });
      }
    },
    [addFormTag, invalidateQueries, selectedRows, toast]
  );

  const removeTag = React.useCallback(
    async (tagId: string) => {
      try {
        if (!selectedRows.hasData())
          throw new Error("Unable to remove tags since no rows are selected");

        // need to delete in batches since we'll hit the limit of the size of the condition
        // and postgres will run out of memory.
        const chunk = _.chunk(selectedRows.data, BATCH_SIZE);
        await Promise.all(
          chunk.map(async (rows) => {
            await removeFormTag({
              body: {
                data: {
                  form_ids: rows.map((row) => row.formId),
                  tag_ids: [tagId],
                },
              },
            });
          })
        );
        setIsDirty(true);
        await invalidateQueries();
      } catch (error) {
        console.error(error);
        toast.error({
          title: "Unable to remove tags",
        });
      }
    },
    [selectedRows, invalidateQueries, removeFormTag, toast]
  );

  const onTagUpdate = React.useCallback(
    async (tagId: string, selected: Selected) => {
      if (selected === "All") {
        applyTagOptimistically(tagId);
        await applyTag(tagId);
        resetOptimisticUpdate(tagId);
      } else if (selected === "None") {
        removeTagOptimistically(tagId);
        await removeTag(tagId);
        resetOptimisticUpdate(tagId);
      }
    },
    [
      applyTagOptimistically,
      applyTag,
      resetOptimisticUpdate,
      removeTagOptimistically,
      removeTag,
    ]
  );

  const setSelectedRowsWrapper = React.useCallback(
    async (selectedRows: RemoteData.RemoteData<unknown, FormId[]>) => {
      setIsDirty(false);
      setSelectedRows(selectedRows);
      if (selectedRows.hasData()) {
        setSelectedFormIds(selectedRows.data.flatMap((row) => [row.formId]));
      }
    },
    []
  );

  const isOrgAdmin = useRequiredHasuraRoles([
    HasuraRole.ADMIN,
    HasuraRole.ORG_ADMIN,
  ]);

  const { setMode, ...tagForm } = useTagForm({
    setIsDirty,
    enrollmentPeriodId,
    setSearchKeyword,
    internalTagGroupId,
  });

  const resetFormTags = React.useCallback(() => {
    setIsDirty(false);
    setSearchKeyword("");
  }, [setSearchKeyword]);

  return {
    tagsProps: {
      searchKeyword,
      tags,
      onTagUpdate,
      onSearch,
      ...tagForm,
      config: {
        title: "Form tags",
        allowNewTag: isOrgAdmin,
        allowDeleteTag: isOrgAdmin,
        allowEditTag: isOrgAdmin,
      },
    },
    setSelectedRows: setSelectedRowsWrapper,
    isDirty,
    resetFormTags,
  };
}

type TagFormProps = {
  enrollmentPeriodId: uuid;
  setSearchKeyword: (keyword: string) => void;
  setIsDirty: (isDirty: boolean) => void;
  internalTagGroupId: uuid | null;
};

function useTagForm({
  enrollmentPeriodId,
  setSearchKeyword,
  setIsDirty,
  internalTagGroupId,
}: TagFormProps) {
  const [error, setError] = React.useState<string | undefined>();
  const initialMode: Mode = {
    type: "List",
  };

  const [mode, setMode] = React.useReducer(
    (_previousMode: Mode, action: Mode) => {
      switch (action.type) {
        case "List":
        case "Edit":
        case "New":
        case "SelectGroup":
          return action;
      }
    },
    initialMode
  );

  const clearError = React.useCallback(() => {
    setError(undefined);
  }, []);

  const newTag = useNewTag({
    setMode,
    enrollmentPeriodId,
    setSearchKeyword,
    setError,
    selectedTagGroupId: internalTagGroupId,
  });

  const editTag = useEditTag({
    setMode,
    setSearchKeyword,
    setError,
  });

  const deleteTag = useDeleteTag({ setIsDirty });

  const handleCloseManageTagGroupDialog = React.useCallback(() => {
    clearError();
  }, [clearError]);

  return {
    mode,
    setMode,
    ...newTag,
    ...editTag,
    ...deleteTag,
    error,
    clearError,
    onCloseManageTagGroupDialog: handleCloseManageTagGroupDialog,
  };
}

function countTags(
  tags: FSR.FetchTagsResponse["data"],
  allTags: FSR.FetchFormTagsResponse["data"]
): Map<uuid, number> {
  // use imperative style for performance
  const counter: Map<uuid, number> = new Map(
    tags.map((tag) => {
      return [tag.id, 0];
    })
  );

  // count form_tag
  for (const currentValue of allTags.flatMap((tag) => tag.tags)) {
    if (currentValue.id === null) continue;

    counter.set(currentValue.id, (counter.get(currentValue.id) ?? 0) + 1);
  }
  return counter;
}
